/**
 * kd-sticky-follow-footer
 *
 * 画面下部に position:fixed で固定された子要素を、サイトフッターに重ならないよう制御する
 * ラッパー。えらんでKuradashi（kd-pantry-sticky-footer）・カート（kd-cart-sticky-checkout）と
 * 同一の押し上げロジック（calcStickyFooterOffset）を用い、PDP の SP 固定カートバーの
 * フッター重なり挙動をそれらに統一する（課題 #12 の追従挙動統一）。
 *
 * 2 種類の対象を扱う:
 *   - 押し上げ対象（data-follow-selector）: フッターが見えた分だけ transform で上へ逃がす
 *     ＝フッターに重ねない。既定は直下の全要素。
 *   - フッター接近時に隠す対象（data-hide-selector）: フッターが少しでも見えたら非表示にする。
 *     PDP のフロート「ページトップへ」ボタン用（サイトフッター内に同等リンクがあり、かつ
 *     押し上げると下部スライダー等のコンテンツに被るため、フッター表示中は隠す）。
 *
 * 常時表示のバーがページ末尾コンテンツ（下部カルーセル等）を隠さないよう、押し上げ対象の
 * 高さ分だけメインコンテナ（data-spacer-selector）末尾に余白を確保する。押し上げた
 * バーはこの余白に収まり、カルーセルもフッターも隠さない（kd-pantry-sticky-footer と同方式）。
 *
 * ホスト自身は tw:contents（レイアウト非参加）のまま利用できるよう、transform / hidden は
 * ホストではなく対象の子要素に適用する（display:contents 要素へ transform を当てても効かない）。
 *
 * @element kd-sticky-follow-footer
 * @attr {string} data-stop-selector   - 押し上げ/隠す基準（サイトフッター）。既定 #shopify-section-v2-footer
 * @attr {string} data-follow-selector - 押し上げ対象。空なら直下の全要素（既定）
 * @attr {string} data-hide-selector   - フッター接近時に隠す対象。空なら対象なし（既定）
 * @attr {string} data-spacer-selector - 末尾余白を付けるメインコンテナ。既定 main#MainContent
 * @see src-lit/components/product/pantry-sticky-footer.logic.ts（calcStickyFooterOffset）
 * @see src-lit/components/product/kd-pantry-sticky-footer.ts
 * @see src-lit/components/cart/kd-cart-sticky-checkout.ts
 */
import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { STATE_CLASSES } from '../../constants/classes';
import { calcStickyFooterOffset } from './pantry-sticky-footer.logic';

@customElement('kd-sticky-follow-footer')
export class KdStickyFollowFooter extends LitElement {
  createRenderRoot() {
    return this;
  }

  /** 押し上げ/隠す判定の基準（サイトフッター）のセレクタ。 */
  @property({ type: String, attribute: 'data-stop-selector' })
  stopSelector = '#shopify-section-v2-footer';

  /** 押し上げ対象のセレクタ。空なら直下の全要素。 */
  @property({ type: String, attribute: 'data-follow-selector' })
  followSelector = '';

  /** フッター接近時（offset > 0）に隠す対象のセレクタ。空なら対象なし。 */
  @property({ type: String, attribute: 'data-hide-selector' })
  hideSelector = '';

  /** 末尾余白（スペーサー）を付けるメインコンテナのセレクタ。 */
  @property({ type: String, attribute: 'data-spacer-selector' })
  spacerSelector = 'main#MainContent';

  private boundOnScroll = this.onScroll.bind(this);

  /** scroll / resize を rAF で間引くためのフラグ。 */
  private ticking = false;

  /** 直近の押し上げ量（変化時のみ DOM を触るため保持）。 */
  private lastOffset = -1;

  /** 直近のスペーサー高さ（変化時のみ padding を書き換えるため保持）。 */
  private lastSpacerHeight = -1;

  /** 押し上げ対象の高さ変化（safe-area / 折り返し / breakpoint 切替）を監視してスペーサーへ反映する。 */
  private resizeObserver: ResizeObserver | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('scroll', this.boundOnScroll, { passive: true });
    window.addEventListener('resize', this.boundOnScroll, { passive: true });

    // 押し上げ対象の高さ分の余白を確保しないと、常時表示のバーがページ末尾コンテンツを隠す。
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.syncSpacer());
      this.followTargets().forEach((el) => this.resizeObserver?.observe(el));
    }
    this.syncSpacer();
    this.update_();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.boundOnScroll);
    window.removeEventListener('resize', this.boundOnScroll);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    // 自分が付けた transform / hidden / 余白は必ず戻す（section 差し替え時に残さない）。
    this.followTargets().forEach((el) => {
      el.style.transform = '';
    });
    this.hideTargets().forEach((el) => {
      el.classList.remove(STATE_CLASSES.HIDDEN);
    });
    const spacer = this.spacerSelector
      ? document.querySelector<HTMLElement>(this.spacerSelector)
      : null;
    if (spacer && this.lastSpacerHeight >= 0) spacer.style.paddingBottom = '';
    this.lastSpacerHeight = -1;
  }

  /**
   * 押し上げ対象の高さ分の余白をメインコンテナ末尾に確保する。
   * バーは常時表示のため、余白が無いとページ末尾コンテンツ（下部カルーセル等）がバーに隠れる。
   * バーは display:none（PC breakpoint）時は高さ 0 = 余白 0 になる。
   */
  private syncSpacer(): void {
    const spacer = this.spacerSelector
      ? document.querySelector<HTMLElement>(this.spacerSelector)
      : null;
    if (!spacer) return;
    const height = this.followTargets().reduce(
      (max, el) => Math.max(max, Math.round(el.getBoundingClientRect().height)),
      0,
    );
    if (height === this.lastSpacerHeight) return;
    this.lastSpacerHeight = height;
    spacer.style.paddingBottom = height > 0 ? `${height}px` : '';
  }

  /** scroll / resize は rAF で間引く（レイアウト読み取りを伴うため）。 */
  private onScroll(): void {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update_();
    });
  }

  /** 押し上げ対象。followSelector 指定時はそれ、無指定なら直下の全要素。 */
  private followTargets(): HTMLElement[] {
    if (this.followSelector) {
      return Array.from(this.querySelectorAll<HTMLElement>(this.followSelector));
    }
    return Array.from(this.children).filter(
      (c): c is HTMLElement => c instanceof HTMLElement,
    );
  }

  /** フッター接近時に隠す対象。hideSelector 無指定なら空。 */
  private hideTargets(): HTMLElement[] {
    if (!this.hideSelector) return [];
    return Array.from(this.querySelectorAll<HTMLElement>(this.hideSelector));
  }

  private update_(): void {
    const footer = this.stopSelector
      ? document.querySelector<HTMLElement>(this.stopSelector)
      : null;
    const offset = footer
      ? calcStickyFooterOffset(footer.getBoundingClientRect().top, window.innerHeight)
      : 0;

    if (offset === this.lastOffset) return;
    this.lastOffset = offset;

    // 押し上げ対象: フッターが見えた分だけ上へ逃がす。
    const transform = offset > 0 ? `translateY(-${offset}px)` : '';
    this.followTargets().forEach((el) => {
      el.style.transform = transform;
    });

    // 隠す対象: フッターが少しでも見えていれば非表示（押し上げでコンテンツに被るのを防ぐ）。
    const hide = offset > 0;
    this.hideTargets().forEach((el) => {
      el.classList.toggle(STATE_CLASSES.HIDDEN, hide);
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-sticky-follow-footer': KdStickyFollowFooter;
  }
}
